import type { IContextualMenuItem } from '@fluentui/react';
import { useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import { useHistory, useLocation } from 'react-router-dom';

import { useAuthzPermissions } from '../../authz/hooks/useAuthzPermissions';
import { useRoles } from '../../authz/providers/RoleProvider';
import { useQueryMonitoringConfig } from '../../mlops/mlops.deployer.hooks';
import { useGranularMLOpsPermissions } from '../../pages/MLOps/userActions';
import { useWorkspaces } from '../../pages/Orchestrator/WorkspaceProvider';
import { RoutePaths } from '../../pages/RoutePaths';
import { ALL_ROUTE_DEFINITIONS, type AuthzPermission } from '../../pages/Routes/routeDefinitions';
import { useCloudPlatformDiscovery, useEnv, useUser } from '../../utils/hooks';
import type { NavigationConfig } from './types';
import { useAppStoreCategories } from './useAppStoreCategories';
import { getNavigationConfig, getUserMenuItems } from './utils';

type UseNavigationConfigParams = {
  onLogoutClick: () => void;
  showAbout: () => void;
};

// Collect all authz permissions from route definitions (computed once)
export const allRoutePermissions = ALL_ROUTE_DEFINITIONS.flatMap((r) => [
  ...(r.requiresAuthzPermissionOR ?? []),
  ...(r.requiresAuthzPermissionAND ?? []),
]);

/** Build a Map from permissions array and results */
export const buildPermissionMap = (
  permissions: readonly AuthzPermission[],
  results: readonly boolean[]
): Map<string, boolean> => {
  const map = new Map<string, boolean>();
  permissions.forEach((p, i) => {
    map.set(p.action, results[i]);
  });
  return map;
};

/** Create a hasPermission checker function from a permission map */
export const createHasPermission = (permissionMap: Map<string, boolean>) => {
  return (permission: AuthzPermission): boolean => permissionMap.get(permission.action) ?? false;
};

export const useNavigationConfig = (params: UseNavigationConfigParams): NavigationConfig => {
  const { onLogoutClick, showAbout } = params;
  const auth = useAuth();
  const env = useEnv();
  const user = useUser();
  const history = useHistory();
  const location = useLocation();
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const { permissions } = useRoles();
  const { ACTIVE_WORKSPACE_NAME } = useWorkspaces();
  const mlopsPermissions = useGranularMLOpsPermissions(ACTIVE_WORKSPACE_NAME);
  const appStoreCategories = useAppStoreCategories();
  const { results: permissionResults, isLoading: isLoadingPermissions } = useAuthzPermissions(allRoutePermissions);

  const mlopsEnabled = Boolean(cloudPlatformDiscovery?.mlopsApiUrl && env?.mlopsUiEnabled);
  const isAuthenticated = !!auth.user?.id_token;

  const monitoringConfigQuery = useQueryMonitoringConfig({
    enabled: Boolean(
      (mlopsEnabled && mlopsPermissions.isAbleToViewDeployment) || mlopsPermissions?.isAbleToViewDeployments
    ),
  });
  const mlopsMonitoringUrl = monitoringConfigQuery.data?.monitoringConfig?.frontendUri;

  const permissionMap = useMemo(() => buildPermissionMap(allRoutePermissions, permissionResults), [permissionResults]);

  const hasPermission = useMemo(() => createHasPermission(permissionMap), [permissionMap]);

  const userMenuItems = useMemo((): IContextualMenuItem[] => {
    if (!env?.menu) return [];

    return getUserMenuItems(env.menu, { hasMenu: false }, onLogoutClick, history, showAbout);
  }, [env?.menu, onLogoutClick, history, showAbout]);

  const isLoading = !env?.menu || isLoadingPermissions;

  const navigationConfig = useMemo((): NavigationConfig => {
    if (isLoading) return { navigationSectionConfig: {}, userMenuItems: [], isLoading: true };

    // When on /appstore, disable exact matching for "All" link to use partial match
    // This ensures the link is marked active when navigating to /appstore
    const isOnAppStore = location.pathname === RoutePaths.APPSTORE;
    const categoriesWithActiveState = appStoreCategories.map((category) => {
      if (category.key === 'all') {
        return {
          ...category,
          exact: !isOnAppStore,
        };
      }
      return category;
    });

    return {
      ...getNavigationConfig({
        activeWorkspaceName: ACTIVE_WORKSPACE_NAME,
        appStoreCategories: categoriesWithActiveState,
        bookTimeLink: env.bookTimeLink,
        cloudPlatformDiscovery,
        env,
        hasAuthzPermission: hasPermission,
        isAuthenticated,
        isLoadingPermissions,
        mlopsMonitoringUrl,
        permissions,
        user,
        userMenu: userMenuItems,
      }),
      isLoading: false,
    };
  }, [
    ACTIVE_WORKSPACE_NAME,
    appStoreCategories,
    cloudPlatformDiscovery,
    env,
    hasPermission,
    isAuthenticated,
    isLoading,
    location.pathname,
    mlopsMonitoringUrl,
    permissions,
    user,
    userMenuItems,
  ]);

  return navigationConfig;
};
